import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { membersKeys } from "@/features/members/queries/membersQueryKeys";
import membersService from "@/features/members/services/membersService";
import type { PromoteToAdminInput, PromoteToAdminResult } from "@/features/members/types";

/**
 * Mutation hook for promoting a member to a web admin.
 * Invalidates members list/detail on success.
 */
export function usePromoteToAdmin() {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async (input: PromoteToAdminInput): Promise<PromoteToAdminResult> => {
            return await membersService.promoteToAdmin(input);
        },
        onError: (error) => {
            const message = error instanceof Error ? error.message : "Failed to promote member. Please try again.";
            if (message.includes("already exists")) {
                toast.error("A member with this email already exists");
            } else if (message.includes("already an admin")) {
                toast.error("This member is already an admin");
            } else {
                toast.error(message);
            }
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({
                queryKey: membersKeys.detail(data.uid),
            });
            queryClient.invalidateQueries({
                queryKey: membersKeys.all,
            });
            toast.success("Member promoted to admin");
        },
    });

    return {
        error: mutation.error,
        isError: mutation.isError,
        isPending: mutation.isPending,
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
    };
}
